import type { ComponentProps } from 'preact';
import { useCallback, useMemo, useState } from 'preact/hooks';
import type { PlaceSearchResult, Stop } from '../../../../arcticapi/index';
import {
  Map,
  type MapBounds,
  type MapMarker,
  type MapViewport,
} from '../../../base/Map/Map';
import { SvgIcon } from '../../../base/Icon/SvgIcon';
import { createPlaceMarker } from '../../../base/Map/PlaceMarker';
import { createStopMarker } from '../../../base/Map/StopMarker';
import styles from './ExploreMap.module.css';

const MIN_STOP_MARKER_ZOOM = 13;
const DEFAULT_SINGLE_COORDINATE_ZOOM = 14;

export interface ExploreMapProps extends ComponentProps<'section'> {
  location: PlaceSearchResult;
  stops: Stop[];
  onBoundsChange?: (bounds: MapBounds) => void;
  onViewportChange?: (viewport: MapViewport) => void;
  onSelectStop: (stopAtcoCode: string) => void;
  singleCoordinateZoom?: number;
}

export function ExploreMap({
  location,
  stops,
  onBoundsChange,
  onViewportChange,
  onSelectStop,
  singleCoordinateZoom = DEFAULT_SINGLE_COORDINATE_ZOOM,
  ...props
}: ExploreMapProps) {
  const [areStopMarkersVisible, setAreStopMarkersVisible] = useState(
    singleCoordinateZoom >= MIN_STOP_MARKER_ZOOM,
  );

  const locationMarker = useMemo<MapMarker>(
    () =>
      createPlaceMarker({
        id: `location:${location.id}`,
        coordinate: location.location.coordinates,
        kind: 'location',
        name: location.commonName,
      }),
    [location.commonName, location.id, location.location.coordinates],
  );

  const stopMarkers = useMemo<MapMarker[]>(
    () =>
      stops.map((stop) =>
        createStopMarker({
          id: stop.atcoCode,
          coordinate: stop.location.coordinates,
          name: stop.commonName,
          mode: stop.stopType,
          bearing: stop.bearing,
        }),
      ),
    [stops],
  );

  const markers = useMemo(
    () =>
      areStopMarkersVisible
        ? [locationMarker, ...stopMarkers]
        : [locationMarker],
    [areStopMarkersVisible, locationMarker, stopMarkers],
  );

  const fitCoordinates = useMemo(
    () => [location.location.coordinates],
    [location.location.coordinates],
  );

  const handleMarkerSelect = useCallback(
    (markerId: string) => {
      if (markerId.startsWith('location:')) {
        return;
      }

      onSelectStop(markerId);
    },
    [onSelectStop],
  );

  const handleViewportChange = useCallback(
    (viewport: MapViewport) => {
      const nextAreStopMarkersVisible = viewport.zoom >= MIN_STOP_MARKER_ZOOM;

      setAreStopMarkersVisible((previousAreStopMarkersVisible) =>
        previousAreStopMarkersVisible === nextAreStopMarkersVisible
          ? previousAreStopMarkersVisible
          : nextAreStopMarkersVisible,
      );

      onBoundsChange?.(viewport.bounds);
      onViewportChange?.(viewport);
    },
    [onBoundsChange, onViewportChange],
  );

  return (
    <Map
      markers={markers}
      fitCoordinates={fitCoordinates}
      singleCoordinateZoom={singleCoordinateZoom}
      onViewportChange={handleViewportChange}
      onMarkerSelect={handleMarkerSelect}
      {...props}
    >
      <div
        className={styles.stopSearchHint}
        data-visible={!areStopMarkersVisible}
        aria-hidden={areStopMarkersVisible}
      >
        <SvgIcon
          name="tooltip"
          size={16}
          className={styles.stopSearchHintIcon}
        />
        <span className={styles.stopSearchHintText}>
          Search or zoom in to view stops
        </span>
      </div>
    </Map>
  );
}
